import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from "@angular/core";
import { MasterDataService } from "./master-data-service";
import { map } from "rxjs";
import { GlobalRepositoryService } from "../core";
import { ITags } from "../features/bucket-items/models/bucket-item.model";
import {
  BucketTypeConstants,
  ErrorConstants,
} from "../features/bucket-items/constants/component-constants";
import { MessageService } from "../main";
import { DataService } from "../features/bucket-items/service/data.service";
import { GenericExtensionsService } from "../extensions/generic-extensions-service";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { NgFor } from "@angular/common";
import { toSignal } from '@angular/core/rxjs-interop';
import { SingleSelectWithSearchComponent } from "./single-select-with-search/single-select-with-search.component";
import { YearPickerComponent } from "./year-picker/year-picker.component";
import { MatFormField, MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";

@Component({
    selector: "app-dynamic-controls",
    templateUrl: "./dynamic-controls.component.html",
    styleUrls: ["./dynamic-controls.component.scss"],
    imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, NgFor, SingleSelectWithSearchComponent, YearPickerComponent, MatFormField, MatInput, MatButton]
})
export class DynamicControlsComponent implements OnInit {
  @Input() initialValue: Partial<ITags>[] = [];
  @Input() bucketType: string;
  @Input() expanded: boolean = false;
  @Input() isMultipleTags: boolean = false;
  @Input() readOnly: boolean = false;
  @Output() controlValues: EventEmitter<Partial<ITags>[]> = new EventEmitter<
    Partial<ITags>[]
  >();
  @Output() cancelOperation: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  private controlsService = inject(MasterDataService);
  private globalRepositoryService = inject(GlobalRepositoryService);
  private messageService = inject(MessageService);
  private dataService = inject(DataService);
  private readonly featureToken = "bucket-items";
  private readonly surfaceToken = "tags";
  readonly permissionList = toSignal(
    this.dataService.getPermissionList().pipe(map(p => (p as string[]) || [])),
    { initialValue: [] as string[] }
  );

  public selectedTagValues: Partial<ITags>[] = [];
  readonly controls = toSignal(
    this.controlsService.GetMasterData().pipe(
      map((controls) =>
        controls
          .filter(control => !control.IsObsolete && this.isFileLevelScope(control.Scope))
          .sort((a, b) => a.TagsDisplayOrder - b.TagsDisplayOrder)
      )
    ),
    { initialValue: [] }
  );

  ngOnInit(): void {
    this.selectedTagValues = [...this.initialValue];
  }

  hasEditAccess(): boolean {
    if (this.readOnly) {
      return false;
    }
    let uiActionPermissionList =
      this.globalRepositoryService.getUserUIActionPermission();
    
    // Check if uiActionPermissionList is undefined or null
    if (!uiActionPermissionList || !Array.isArray(uiActionPermissionList)) {
      return false;
    }

    const requiredPermission = GenericExtensionsService.getRequiredUpdateTagPermission(this.bucketType);
    if (!requiredPermission) {
      return false;
    }

    return uiActionPermissionList.some(
      (x) => (x?.UIPermissionIdentifier || "").toLowerCase() === requiredPermission
    );
  }

  getInitialTagValue(tagKey: string): string {
    return this.initialValue.find((x) => x.TagKey === tagKey)?.TagValue || null;
  }

  setTagValue(selectedTag: Partial<ITags>, tagKey: string) {
    let index = this.selectedTagValues.findIndex((x) => x.TagKey === tagKey);
    if (index >= 0) {
      this.selectedTagValues.splice(index, 1);
    }
    this.selectedTagValues.push(selectedTag);
  }

  onSubmit() {
    if(this.shouldDisableTagOptions()){
      return;
    }
    if(this.initialValue.length===0 && this.selectedTagValues.every(x=>x.TagValue==='') || this.selectedTagValues.every(x=>x.TagValue==='') && this.initialValue.every(x=>x.TagValue==='')){
      this.messageService.alert(
        ErrorConstants.UPDATE_TAG_VALUE);
    }
    else if(!this.areArrayEquualSorted(this.selectedTagValues.sort(),this.initialValue.sort())){
      if(this.isMultipleTags){
        this.selectedTagValues= this.selectedTagValues.filter(x=>{
          return x.TagValue!=="";
        });
      }
      this.controlValues.emit(
        this.selectedTagValues.filter(tag => !this.isProtectedMetadataTag(tag.TagKey))
      );
    }
    else{
      this.messageService.alert(
        ErrorConstants.UPDATE_TAG_VALUE);
    }
  }

  private areArrayEquualSorted(arr1: Partial<ITags>[], arr2: Partial<ITags>[]): boolean{
    if(JSON.stringify(arr1.sort((a:any,b:any)=>a.TagsDisplayOrder-b.TagsDisplayOrder)) === JSON.stringify(arr2.sort((a:any,b:any)=>a.TagsDisplayOrder-b.TagsDisplayOrder))){
      return true;
    }
    else{
      return false;
    }
  }


  public cancel() {
    this.cancelOperation.emit(true);
  }

  public buildTagIdPrefix(tagKey: string): string {
    return `${this.featureToken}-${this.surfaceToken}-tag-${this.slugify(tagKey)}`;
  }

  public buildReadOnlyFieldId(tagKey: string, index?: number): string {
    const suffix = index !== undefined ? `-${index.toString().padStart(2, "0")}` : "";
    return `${this.featureToken}-${this.surfaceToken}-fld-readonly-${this.slugify(tagKey)}${suffix}`;
  }

  public buildActionId(element: string, state?: string): string {
    const context = state ?? (this.isMultipleTags ? "bulk" : "single");
    return `${this.featureToken}-${this.surfaceToken}-${element}-${context}`;
  }

  public shouldDisableTagOptions(): boolean {
    if (this.readOnly) {
      return true;
    }
    const requiredPermission = GenericExtensionsService.getRequiredUpdateTagPermission(this.bucketType);
    if (!requiredPermission) {
      return false;
    }

    const normalizedPermissions = (this.permissionList() ?? []).map((permission) =>
      (permission || "").toLowerCase()
    );

    return !normalizedPermissions.some((permission) =>
      permission.includes(requiredPermission)
    );
  }

  public shouldDisableTagControl(tagKey: string): boolean {
    if (this.shouldDisableTagOptions()) {
      return true;
    }

    if (this.isProtectedMetadataTag(tagKey)) {
      return true;
    }

    if (
      this.bucketType === BucketTypeConstants.PREARCHIVE &&
      this.isRawDataOrValidityTag(tagKey)
    ) {
      return true;
    }

    if (
      this.bucketType === BucketTypeConstants.FACILITY_ARCHIVE &&
      this.isRawDataOrValidityTag(tagKey)
    ) {
      return true;
    }

    return false;
  }

  private isRawDataOrValidityTag(tagKey: string): boolean {
    const normalizedTagKey = (tagKey || "").trim().toLowerCase();
    return normalizedTagKey === "raw data" || normalizedTagKey === "validity";
  }

  private isProtectedMetadataTag(tagKey?: string): boolean {
    const normalizedTagKey = (tagKey || "").trim().toLowerCase();
    return normalizedTagKey === "source" ||
      normalizedTagKey === "software name" ;
  }

  private isFileLevelScope(scope?: string): boolean {
    const normalizedScope = `${scope ?? ""}`.trim().toLowerCase();
    return normalizedScope === "filelevel";
  }

  private slugify(value: string): string {
    return (value || "unknown")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-");
  }
}
